'use server'

import { prisma } from '@/lib/prisma'
import { redirect } from 'next/navigation'
import { cookies } from 'next/headers'
import bcrypt from 'bcryptjs'
import { encrypt } from '@/lib/auth'

const SALT_ROUNDS = 10

export async function signup(prevState: any, formData: FormData) {
    // 1. Honeypot Check
    const honeypot = formData.get('zip_code_verify') // Hidden field
    if (honeypot) {
        console.warn('Bot detected via honeypot')
        return { error: 'Invalid request' }
    }

    const email = formData.get('email') as string
    const password = formData.get('password') as string
    const name = formData.get('name') as string

    if (!email || !password || !name) {
        return { error: 'Missing Required Fields' }
    }

    // 2. Check if user exists
    const existingUser = await prisma.user.findUnique({
        where: { email }
    })

    if (existingUser) {
        return { error: 'User already exists' }
    }

    // 3. Create User
    const hashedPassword = await bcrypt.hash(password, SALT_ROUNDS)

    // First user is Admin? optional logic. For now, default is USER.
    // Check if any user exists, if not make ADMIN.
    const userCount = await prisma.user.count()
    const role = userCount === 0 ? 'ADMIN' : 'USER'

    const user = await prisma.user.create({
        data: {
            email,
            password: hashedPassword,
            name,
            role
        }
    })

    // 4. Create Session
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000)
    const session = await encrypt({ userId: user.id, email: user.email, role: user.role })

    const cookieStore = await cookies()
    cookieStore.set('session', session, { expires, httpOnly: true, secure: true, sameSite: 'lax' })

    redirect('/')
}

export async function login(prevState: any, formData: FormData) {
    // 1. Honeypot Check
    const honeypot = formData.get('zip_code_verify')
    if (honeypot) {
        console.warn('Bot detected via honeypot')
        return { error: 'Invalid request' }
    }

    const email = formData.get('email') as string
    const password = formData.get('password') as string

    // 2. Find User
    const user = await prisma.user.findUnique({
        where: { email }
    })

    if (!user) {
        return { error: 'Invalid credentials' }
    }

    // 3. Verify Password
    const isValid = await bcrypt.compare(password, user.password)
    if (!isValid) {
        return { error: 'Invalid credentials' }
    }

    // 4. Create Session
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000)
    const session = await encrypt({ userId: user.id, email: user.email, role: user.role })

    const cookieStore = await cookies()
    cookieStore.set('session', session, { expires, httpOnly: true, secure: true, sameSite: 'lax' })

    redirect(user.role === 'ADMIN' ? '/admin' : '/')
}

export async function logout() {
    const cookieStore = await cookies()
    cookieStore.delete('session')
    redirect('/login')
}
